import { ApiHandler } from '../ApiHandler'

/** Ключ API компании (раздел «Интеграции»). Сам ключ приходит только при создании. */
export interface IntegrationKey {
  id: number
  name: string
  key_prefix: string
  owner_name: string
  webhook_url: string | null
  created_at: string | null
  last_used_at: string | null
  revoked_at: string | null
  active: boolean
}

export interface IntegrationKeyCreated extends IntegrationKey {
  key: string
  webhook_secret: string | null
}

export interface IntegrationLogRow {
  at: string
  method: string
  path: string
  status: number
}

/** Страница ссылки встречи интеграции (/m/<код>) до входа. */
export interface IntegrationLinkInfo {
  role: 'host' | 'guest'
  title: string
  starts_at: string
  ends_at: string
  name: string | null
  /** revoked — ссылка заменена новой (перевыпуск), вход закрыт. */
  state: 'open' | 'canceled' | 'ended' | 'revoked'
  message: string | null
  /** Есть у окна ведущего из CRM: 'host' — запись включает ведущий. */
  recording_policy?: string
  /** false — гостю не предлагать запись на почту (встреча из CRM); нет поля — как раньше. */
  recording_by_email?: boolean
  /** stored — чат встречи сохраняется и идёт через API (встреча из CRM); нет поля — как раньше. */
  chat_policy?: 'ephemeral' | 'stored'
  /** Встреча из CRM: клиент входит в комнату, не дожидаясь ведущего. */
  join_without_host?: boolean
  /** Встреча из CRM: hidden — портал скрыл индикатор «Идёт запись» (предупреждает менеджер). */
  recording_indicator?: 'shown' | 'hidden'
  /** Окно ведущего из CRM при stored: своя метка автора в переписке. */
  chat?: { my_tag: string }
}

export abstract class IntegrationApi {
  public static async access(): Promise<{ can_manage: boolean; company: string | null; api_url: string }> {
    return await ApiHandler.get('integrations/access')
  }

  public static async keys(): Promise<{ data: IntegrationKey[] }> {
    return await ApiHandler.get('integrations/keys')
  }

  public static async createKey(payload: { name: string; webhook_url?: string }): Promise<IntegrationKeyCreated> {
    return await ApiHandler.post('integrations/keys', payload)
  }

  public static async revokeKey(id: number): Promise<IntegrationKey> {
    return await ApiHandler.delete(`integrations/keys/${id}`, {})
  }

  public static async logs(id: number): Promise<{ data: IntegrationLogRow[] }> {
    return await ApiHandler.get(`integrations/keys/${id}/logs`)
  }

  // --- Публично: ссылки ведущего и гостя ---

  public static async linkInfo(code: string): Promise<IntegrationLinkInfo> {
    return await ApiHandler.get(`integrations/links/${encodeURIComponent(code)}`)
  }

  /**
   * Ответ того же вида, что у гостевого входа: токен, ожидание начала или зал ожидания.
   * chat_token — прежний токен переписки этой встречи: с ним гость остаётся тем же
   * автором; при stored к токену LiveKit добавляется chat: {token, my_tag}.
   */
  public static async join(code: string, payload: { name?: string; admission_token?: string; chat_token?: string }) {
    return await ApiHandler.post(`integrations/links/${encodeURIComponent(code)}/join`, payload)
  }

  public static async hostWaiting(code: string) {
    return await ApiHandler.get(`integrations/links/${encodeURIComponent(code)}/waiting`)
  }

  public static async hostAdmit(code: string, id: number) {
    return await ApiHandler.post(`integrations/links/${encodeURIComponent(code)}/admissions/${id}/admit`, {})
  }

  public static async hostDeny(code: string, id: number) {
    return await ApiHandler.post(`integrations/links/${encodeURIComponent(code)}/admissions/${id}/deny`, {})
  }
}
